import { FC } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useForm, useStore } from '@tanstack/react-form';
import { observer } from 'mobx-react';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import BaseConfig from './BaseConfig';
import useCompanyId from '../../../hooks/useCompanyId';
import { VivaISVTurnover, VivaISVTurnoverMap } from '../../../constants/isv';
import useIsSubscriptionActive from '../../../hooks/useIsSubscriptionActive';
import {
  useCreateVivaIntegration,
  useIntegrationsList,
  useUpdateVivaIntegrationMutation,
} from '../../../stores/FetcherStores/integrationFetcher';
import { usePaymentMethodForm } from '../../paymethod/fields/usePaymentMethodForm';
import {
  DefaultNamesForPaymentMethodConfigTypes,
  DefaultShortNamesForPaymentMethodConfigTypes,
  PaymentMethodConfigTypes,
} from '../../../model/PaymentMethodConfig';
import {
  FeeTypes,
  VivaWalletPaymentMethod,
  VivaWalletTerminalPaymentMethod,
} from '../../../model/PaymentMethod';
import { useCreatePaymentMethodMutation } from '../../../stores/FetcherStores/PaymentMethodFetcher';
import SharedPaymentMethodFieldGroup from '../../paymethod/fields/SharedPaymentMethodFieldGroup';
import { IntegrationViva } from '../../../model/Integration';
import VivaTerminalFieldGroup from '../../paymethod/fields/VivaTerminalFieldGroup';
import VivaSupport from '../../../views/Company/integrations/VivaSupport';

const VivaPrice = ({
  totalIsvFee,
  hasEnklaorderSubscription,
}: {
  totalIsvFee: number;
  hasEnklaorderSubscription: boolean;
}) => {
  return (
    <Grid item xs={12} md={12}>
      <Typography variant="h5" gutterBottom>
        Viva.com avgifter
      </Typography>
      <Typography>
        Debit* {Math.round((0.57 + totalIsvFee) * 100) / 100}% (minimum 0.36kr)
      </Typography>
      <Typography>
        Kredit* {Math.round((0.67 + totalIsvFee) * 100) / 100}% (minimum 0.36kr)
      </Typography>
      <Typography>
        Corporate* {Math.round((1.57 + totalIsvFee) * 100) / 100}% (minimum 0.36kr)
      </Typography>
      <Typography>* Gäller alla EU / ESS kort, inte bara Sverige.</Typography>
      <Typography>
        Enklaorder selfservice:{' '}
        {Math.round((1 + (hasEnklaorderSubscription ? 0.49 : 4.0)) * 100) / 100}%
      </Typography>
      <Typography>
        Enklaorder dela nota:{' '}
        {Math.round((1 + (hasEnklaorderSubscription ? 0.49 : 1.0)) * 100) / 100}%{' '}
      </Typography>
      {!hasEnklaorderSubscription && (
        <Typography>
          Med Enklaorder prenumeration (499kr/mån) ligger avgiften på 1.49%. Då kan även swish
          handel användas
        </Typography>
      )}
      <Typography>
        Månadskostnad från 58kr/mån, faktureras direkt till kunden från Viva.com. Läs mer på{' '}
        <Link to="https://www.viva.com/sv-se/pricing" target="_blank" rel="noopener noreferrer">
          Viva.com
        </Link>
      </Typography>
      <Typography color="secondary" fontWeight="bold">
        Profit share ca. {Math.round(totalIsvFee * 0.2 * 1000) / 1000}%
      </Typography>
    </Grid>
  );
};

const VivaIntegration = observer(() => {
  const { t } = useTranslation();
  const hasEnklaorderSubscription = useIsSubscriptionActive('ENKLA_ORDER');
  const companyId = useCompanyId();
  const { data: integrations } = useIntegrationsList(companyId);

  const vivaIntegration = integrations?.find(
    (integration) => integration.type === 'VIVA',
  ) as IntegrationViva;
  const {
    mutateAsync: createVivaIntegration,
    isLoading: isCreating,
    error: createError,
  } = useCreateVivaIntegration();
  const {
    mutateAsync: updateVivaIntegration,
    isLoading: isUpdating,
    error: updateError,
  } = useUpdateVivaIntegrationMutation();
  const form = useForm({
    defaultValues: {
      turnover: vivaIntegration?.turnover ?? VivaISVTurnover.DEFAULT,
      additionalFee: vivaIntegration?.terminalFee?.amount
        ? vivaIntegration.terminalFee.amount -
          VivaISVTurnoverMap[vivaIntegration?.turnover || VivaISVTurnover.DEFAULT]
        : '0',
    },
  });

  const error = createError || updateError;
  const errorMessage =
    error instanceof AxiosError ? error?.response?.data?.name || error?.message : error?.message;

  const turnover = useStore(form.store, (state) => state.values.turnover);
  const additionalFee = useStore(form.store, (state) => state.values.additionalFee);

  const totalIsvFee =
    VivaISVTurnoverMap[turnover] +
    (Number.isNaN(Number(additionalFee)) ? 0 : Number(additionalFee));

  return (
    <>
      <Grid item xs={12}>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h4" gutterBottom>
          Integration
        </Typography>
      </Grid>
      <VivaPrice totalIsvFee={totalIsvFee} hasEnklaorderSubscription={hasEnklaorderSubscription} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} sx={{ mt: 2 }}>
          <form.Field name="turnover">
            {(field) => (
              <FormControl sx={{ marginBottom: 1 }} fullWidth>
                <InputLabel id="isv-fee-label">{t('vivaWallet.revenue')}</InputLabel>
                <Select
                  labelId="isv-fee-label"
                  id="isv-fee-select"
                  value={field.state.value}
                  label={t('vivaWallet.revenue')}
                  onChange={(e) => {
                    field.handleChange(e.target.value as VivaISVTurnover);
                  }}
                >
                  {Object.entries(VivaISVTurnover).map(([key, value]) => (
                    <MenuItem key={key} value={key}>
                      {t(`vivaWallet.ISVRevenue.${value}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </form.Field>
        </Grid>
        <Grid item xs={12} md={6} sx={{ mt: 2 }}>
          <form.Field
            name="additionalFee"
            validators={{
              onChange: ({ value }) => {
                if (!value || Number.isNaN(Number(value))) {
                  return 'Måste vara ett nummer';
                }
                if (Number(value) < 0) {
                  return 'Måste vara större än 0';
                }
                return null;
              },
            }}
          >
            {(field) => (
              <FormControl sx={{ marginBottom: 1 }} fullWidth>
                <TextField
                  label={t('vivaWallet.additionalFee')}
                  value={field.state.value}
                  onChange={(e) => {
                    field.handleChange(e.target.value.replace(',', '.'));
                  }}
                  error={field.state.meta.errors.length > 0}
                  helperText={
                    field.state.meta.errors.length > 0
                      ? field.state.meta.errors.join(', ')
                      : 'Ange valfritt tillägg i procent'
                  }
                />
              </FormControl>
            )}
          </form.Field>
        </Grid>
        <Grid item xs={12} md={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={() => {
              if (vivaIntegration) {
                updateVivaIntegration(companyId, vivaIntegration.id, {
                  turnover,
                  terminalFee: {
                    amount: totalIsvFee,
                    type: FeeTypes.PERCENTAGE,
                  },
                });
              } else {
                createVivaIntegration(companyId, {
                  turnover,
                  terminalFee: totalIsvFee,
                });
              }
            }}
            disabled={isCreating || isUpdating}
            color="secondary"
          >
            {vivaIntegration ? 'Spara' : 'Sätt upp'}
            {isCreating || isUpdating ? <CircularProgress size={20} /> : null}
          </Button>
        </Grid>
      </Grid>
    </>
  );
});

interface VivaConfigProps {
  onClose: () => void;
  isTerminal?: boolean;
}

const VivaConfig: FC<VivaConfigProps> = observer(({ onClose, isTerminal }) => {
  const companyId = useCompanyId();
  const { data: integrations } = useIntegrationsList(companyId);

  const vivaIntegration = integrations?.find(
    (integration) => integration.type === 'VIVA',
  ) as IntegrationViva;
  const {
    mutateAsync: createPaymentMethod,
    isLoading: isCreating,
    error: createError,
  } = useCreatePaymentMethodMutation();

  const form = usePaymentMethodForm({
    defaultValues: {
      baseFields: {
        name: DefaultNamesForPaymentMethodConfigTypes[PaymentMethodConfigTypes.VIVA_WALLET],
        shortName:
          DefaultShortNamesForPaymentMethodConfigTypes[PaymentMethodConfigTypes.VIVA_WALLET],
        active: true,
        bookkeepingAccount: '',
      },
      terminalFields: {
        terminalId: '',
        merchantAccountId: '',
      },
    },
    onSubmit: async ({ value }) => {
      if (isTerminal) {
        const newPaymentMethod: Omit<
          VivaWalletTerminalPaymentMethod,
          'id' | 'accountId' | 'onboardingStatus' | 'onboardingUrl'
        > = {
          type: PaymentMethodConfigTypes.VIVA_WALLET_TERMINAL,
          name: value.baseFields.name,
          shortName: value.baseFields.shortName,
          active: value.baseFields.active,
          bookkeepingAccount: value.baseFields.bookkeepingAccount,
          terminalId: value.terminalFields.terminalId,
          terminalMerchantId: value.terminalFields.merchantAccountId,
        };
        await createPaymentMethod(companyId, newPaymentMethod)
          .then(() => {
            onClose();
          })
          .catch(() => {});
      } else {
        const newPaymentMethod: Omit<
          VivaWalletPaymentMethod,
          'id' | 'accountId' | 'onboardingStatus' | 'onboardingUrl'
        > = {
          type: PaymentMethodConfigTypes.VIVA_WALLET,
          name: value.baseFields.name,
          shortName: value.baseFields.shortName,
          active: value.baseFields.active,
          bookkeepingAccount: value.baseFields.bookkeepingAccount,
        };
        await createPaymentMethod(companyId, newPaymentMethod)
          .then(() => {
            onClose();
          })
          .catch(() => {});
      }
    },
  });

  return (
    <BaseConfig
      title="Kortbetalning - Viva.com"
      description="Den mest flexibla kortbetalningslösningen som funkar på alla enhter och på webben. Konfigurera dina Viva.com inställningar för att komma igång."
      price=""
      onClose={onClose}
      onSubmit={() => {
        form.handleSubmit();
      }}
      errorMessage={
        createError instanceof AxiosError
          ? createError?.response?.data?.name || createError?.message
          : createError?.message
      }
      buttons={
        <>
          <Button variant="outlined" onClick={onClose} color="secondary">
            Avbryt
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isCreating}
            sx={{
              backgroundColor: '#1db954',
              '&:hover': {
                backgroundColor: '#1a9f4a',
              },
            }}
          >
            {isCreating ? 'Lägger till...' : 'Lägg till'}
          </Button>
        </>
      }
    >
      <Grid item xs={12}>
        <Accordion
          defaultExpanded={!vivaIntegration}
          sx={{
            '&:before': { display: 'none' },
            boxShadow: 'none',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              backgroundColor: '#f5f5f5',
              '&:hover': { backgroundColor: '#eeeeee' },
            }}
          >
            <Typography variant="h6">
              {vivaIntegration
                ? 'Integration (Redan konfigurerad)'
                : 'Integration (Krävs för att fortsätta)'}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <VivaIntegration />
            <VivaSupport />
          </AccordionDetails>
        </Accordion>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h4" gutterBottom>
          Betalmetod
        </Typography>
      </Grid>
      {vivaIntegration ? (
        <form.AppForm>
          <SharedPaymentMethodFieldGroup form={form} fields="baseFields" />
          {isTerminal && <VivaTerminalFieldGroup form={form} fields="terminalFields" />}
          <VivaSupport />
        </form.AppForm>
      ) : (
        <Grid item xs={12}>
          <Alert severity="info">
            <Typography>Sätt upp integration ovan först, skapa sedan betalmetoden här</Typography>
          </Alert>
        </Grid>
      )}
    </BaseConfig>
  );
});

export default VivaConfig;
